import _ from "lodash";
import * as projectTypes from "@/helpers/projectTypes";
import * as projectAddons from "@/helpers/projectAddons";

const initialState = () => ({
  current_step: 1,
  last_step: 6,
  current_substep: 1,
  last_substep: 5,
  states: [],
  cities: [],
  building_descriptions: [],
  surrounding_terrains: [],
  project_types: [],
  project_addons: [],
  roof_types: [],
  shipping_profiles: [],
  id: null,
  activity_id: null,
  formdata: {
    location: {
      address: {
        address_1: "",
        address_2: "",
        state: "",
        city: "",
        county: "",
        postal_code: "",
        latitude: null,
        longitude: null,
        country: ""
      }
    },
    project_meta: {
      building_description: {},
      mount_type: {},
      surrounding_terrain: {},
      roof_type: {},
      type_pitch: null,
      roof_slope: null,
      building_width: null,
      building_length: null,
      number_of_stories: null,
      number_of_panels: null,
      roof_type_actual: null,
      bess_anchorage_type: null,
      full_calcs: {
        array: []
      },
      designer: {
        array: [],
        project: {
          permitting_project_type: null,
          customer_name: null
        },
        contractor: {
          license_number: null,
          contractor_name: null,
          address: {
            address_1: "",
            state: "",
            city: "",
            county: "",
            postal_code: "",
            latitude: null,
            longitude: null,
            country: ""
          },
          expiration_date: null,
          phone: null,
          email: null
        },
        equipment_list: {
          solar_plant_size: null,
          number_of_panels: null,
          number_of_optimizers: null,
          pv_module_manufacturer: null,
          pv_module_model_name: null,
          number_of_inverter: null,
          inverter_manufacturer: null,
          inverter_model_name: null,
          optimizer_manufacturer: null,
          optimizer_model_name: null,
          battery_storage: null,
          battery_manufacturer: null,
          equipment_notes: null
        },
        electrical: {
          msp_busbar_rating: null,
          msp_breaker_rating: null,
          msp_upgrade_required: null,
          feeder_type: null,
          interconnection_method: null,
          ac_disconnect_required: null,
          production_meter_required: null,
          electrical_notes: null,
          panels: [
            {
              breaker_rating: null,
              busbar_rating: null
            }
          ],
          service_voltage: null
        },
        structural: {
          wind_exposure: null,
          roofing_material: null,
          racking_make: null,
          racking_model: null,
          attachment_pattern: null,
          attachment_make: null,
          attachment_model: null,
          additional_information: null,
          roof_type: null,
          building_width: null,
          building_length: null,
          number_of_stories: null
        }
      }
    },
    type: {
      project_types: [],
      project_addons: [],
      wet_stamps: 2,
      shipping_profile: {},
      save_shipping_profile: true,
      set_default_shipping_profile: false,
      pzse_express_agree: null
    },
    details: {
      name: "",
      number: "",
      client_po: "",
      description: ""
    },
    summary: {
      promo_codes: []
    }
  },
  account_balance: {},
  costing: {
    sub_total_cost: "0.00",
    total_cost: "0.00",
    promo: null,
    additional_credits: "0.00",
    project_types_cost: [],
    project_addons_cost: {}
  },
  sub_total_cost: "0.00",
  total_cost: "0.00",
  promos: [],
  additional_credits: "0.00",
  current_balance: "0.00",
  current_credits: "0.00",
  remaining_credits: "0.00",
  remaining_balance: "0.00",
  project_creation_progress: {},
  shipping_profile_id: null,
  project_documents_misc: {},
  electrical_upload_confirmation_enabled: false,
  calculating: false,
  calculation_delay_in_sec: 1.5,
  possible_cities: null,
  full_project_addr: "",
  possible_cities_contractor: null,
  full_project_addr_contractor: "",
  has_upload_structural_plans: null,
  has_upload_pv_panel_layout: null,
  project_documents_structural_plans: {},
  project_documents_pv_panel_layout: {},
  misc_queue_completed: false,
  sp_queue_completed: false,
  ppl_queue_completed: false,
  is_express_disabled: false,
  engineering_values: {}
});

export default {
  namespaced: true,
  state: initialState,
  getters: {
    is_project_done: state => {
      return state.current_step === state.last_step;
    },
    is_planset_integration_done: state => {
      return state.current_substep === state.last_substep;
    },
    is_formdata_rooftype_value_other: state => {
      return state.formdata.project_meta.roof_type?.name === "Other";
    },
    is_express_selected: state => {
      return _.find(
        state.formdata.type.project_addons,
        item => item.name === "PZSE Express"
      );
    },
    is_residential_yard: state => {
      const name = state.formdata.project_meta.mount_type?.name;
      return name?.startsWith("Ground Mount") || false;
    },

    is_ground_mount: state => {
      const name = state.formdata.project_meta.mount_type?.name;
      return name?.startsWith("Ground Mount") || false;
    },

    is_ballasted: state => {
      const name = state.formdata.project_meta.mount_type?.name;
      return name === "Ballasted" || false;
    },

    wet_stamp_price: state => {
      const count = state.formdata.type.wet_stamps;
      const wetstamp1to4 = projectAddons.wetStampOneToFour();
      const wetstamp5to8 = projectAddons.wetStampFiveToEight();

      if (_.inRange(count, 5, 9)) {
        return state.project_addons.find(addon => addon.id === wetstamp5to8.id);
      }

      return state.project_addons.find(addon => addon.id === wetstamp1to4.id);
    },
    is_iebc_letter: state => {
      const project_types = _.get(state, "formdata.type.project_types", []);
      const projectType = projectTypes.iebcLetter();

      if (projectType) {
        return (
          project_types.length >= 1 &&
          !!_.find(project_types, { id: projectType.value })
        );
      }

      return false;
    },
    is_full_structural: state => {
      const project_types = _.get(state, "formdata.type.project_types", []);
      const projectType = projectTypes.fullStructural();

      if (projectType) {
        return (
          project_types.length >= 1 &&
          !!_.find(project_types, { id: projectType.value })
        );
      }

      return false;
    },
    is_electrical_stamp: state => {
      const project_types = _.get(state, "formdata.type.project_types", []);
      const projectType = projectTypes.electricalStamp();

      if (projectType) {
        return (
          project_types.length >= 1 &&
          !!_.find(project_types, { id: projectType.value })
        );
      }

      return false;
    },
    is_electrical_fault_study: state => {
      const project_types = _.get(state, "formdata.type.project_types", []);
      const projectType = projectTypes.electricalFaultStudy();

      if (projectType) {
        return (
          project_types.length >= 1 &&
          !!_.find(project_types, { id: projectType.value })
        );
      }

      return false;
    },
    is_full_structural_only: state => {
      const project_types = _.get(state, "formdata.type.project_types", []);
      const projectType = projectTypes.fullStructural();

      if (projectType) {
        return (
          project_types.length == 1 &&
          !!_.find(project_types, { id: projectType.value })
        );
      }

      return false;
    },
    is_planset_design: state => {
      const project_types = _.get(state, "formdata.type.project_types", []);
      const projectType = projectTypes.plansetDesign();

      if (projectType) {
        return (
          project_types.length >= 1 &&
          !!_.find(project_types, { id: projectType.value })
        );
      }

      return false;
    }
  },
  mutations: {
    SET_CURRENT_STEP: (state, payload) => (state.current_step = payload),
    SET_CURRENT_SUBSTEP: (state, payload) => (state.current_substep = payload),

    // Set predefined values.
    SET_STATES: (state, payload) => (state.states = payload),
    SET_CITIES: (state, payload) => (state.cities = payload),
    SET_BUILDING_DESCRIPTIONS: (state, { building_descriptions }) =>
      (state.building_descriptions = building_descriptions),
    SET_SURROUNDING_TERRAINS: (state, { surrounding_terrains }) =>
      (state.surrounding_terrains = surrounding_terrains),
    SET_PROJECT_TYPES: (state, { project_types }) =>
      (state.project_types = project_types),
    SET_PROJECT_ADDONS: (state, { project_addons }) =>
      (state.project_addons = project_addons),
    SET_ROOF_TYPES: (state, { roof_types }) => (state.roof_types = roof_types),
    SET_SHIPPING_PROFILES: (state, { shipping_profiles }) =>
      (state.shipping_profiles = shipping_profiles),

    // Set project relevant IDs.
    SET_ID: (state, payload) => (state.id = payload),
    SET_ACTIVITY_ID: (state, payload) => (state.activity_id = payload),

    // Set formdata values.
    SET_FORMDATA_LOCATION: (state, payload) =>
      (state.formdata.location = payload),

    SET_FORMDATA_PROJECT_META: (state, payload) =>
      (state.formdata.project_meta = payload),

    SET_FORMDATA_BUILDING_DESCRIPTION: (state, payload) =>
      (state.formdata.project_meta.building_description = payload),

    SET_FORMDATA_MOUNT_TYPE: (state, payload) =>
      (state.formdata.project_meta.mount_type = payload),

    SET_FORMDATA_SURROUNDING_TERRAIN: (state, payload) =>
      (state.formdata.project_meta.surrounding_terrain = payload),

    SET_FORMDATA_ROOF_TYPE: (state, payload) =>
      (state.formdata.project_meta.roof_type = payload),

    SET_FORMDATA_ROOF_SLOPE: (state, payload) =>
      (state.formdata.project_meta.roof_slope = payload),

    SET_FORMDATA_TYPE_PITCH: (state, payload) =>
      (state.formdata.project_meta.type_pitch = payload),

    SET_FORMDATA_TYPE_PROJECT_TYPES: (state, { project_types }) =>
      (state.formdata.type.project_types = _.orderBy(
        project_types,
        ["name"],
        ["asc"]
      )),
    SET_FORMDATA_TYPE_PROJECT_ADDONS: (state, { project_addons }) =>
      (state.formdata.type.project_addons = _.orderBy(
        project_addons,
        ["name"],
        ["asc"]
      )),
    SET_FORMDATA_TYPE_SHIPPING_PROFILE: (state, { shipping_profile }) =>
      (state.formdata.type.shipping_profile = shipping_profile),
    SET_FORMDATA_TYPE_PZSE_EXPRESS_AGREE: (state, { pzse_express_agree }) =>
      (state.formdata.type.pzse_express_agree = pzse_express_agree),
    SET_FORMDATA_TYPE_SAVE_SHIPPING_PROFILE: (
      state,
      { save_shipping_profile }
    ) => (state.formdata.type.save_shipping_profile = save_shipping_profile),
    SET_FORMDATA_TYPE_SET_DEFAULT_SHIPPING_PROFILE: (
      state,
      { set_default_shipping_profile }
    ) =>
      (state.formdata.type.set_default_shipping_profile = set_default_shipping_profile),
    SET_FORMDATA_TYPE_WET_STAMPS: (state, { wet_stamps }) =>
      (state.formdata.type.wet_stamps = wet_stamps),
    SET_FORMDATA_DETAILS: (state, payload) =>
      (state.formdata.details = payload),

    // Set balance values.
    SET_ACCOUNT_BALANCE: (state, payload) => (state.account_balance = payload),

    // Set total cost.
    SET_TOTAL_COST: (state, payload) => (state.total_cost = payload),

    SET_SUB_TOTAL_COST: (state, payload) => (state.sub_total_cost = payload),

    SET_PROMOS: (state, payload) => (state.promos = payload),

    SET_COSTING: (state, payload) => (state.costing = payload),

    SET_ADDITIONAL_CREDITS: (state, payload) =>
      (state.additional_credits = payload),

    // Set current balance.
    SET_CURRENT_BALANCE: (state, payload) => (state.current_balance = payload),

    SET_CURRENT_CREDITS: (state, payload) => (state.current_credits = payload),

    // Set remaining balance.
    SET_REMAINING_BALANCE: (state, payload) =>
      (state.remaining_balance = payload),

    SET_REMAINING_CREDITS: (state, payload) =>
      (state.remaining_credits = payload),

    // Set project creation progress.
    SET_PROJECT_CREATION_PROGRESS: (state, payload) =>
      (state.project_creation_progress = payload),

    SET_PROJECT_CREATION_PROGRESS_DETAILS: (state, payload) =>
      (state.project_creation_progress.details = payload),

    SET_SHIPPING_PROFILE_ID: (state, payload) =>
      (state.shipping_profile_id = payload),

    SET_PROJECT_DOCUMENTS_SP: (state, payload) =>
      (state.project_documents_structural_plans = payload),

    SET_PROJECT_DOCUMENTS_PPL: (state, payload) =>
      (state.project_documents_pv_panel_layout = payload),

    SET_PROJECT_DOCUMENTS_MISC: (state, payload) =>
      (state.project_documents_misc = payload),

    SET_HAS_UPLOAD_STRUCTURAL_PLANS: (state, payload) =>
      (state.has_upload_structural_plans = payload),

    SET_HAS_UPLOAD_PV_PANEL_LAYOUT: (state, payload) =>
      (state.has_upload_pv_panel_layout = payload),

    SET_MISC_QUEUE_COMPLETED: (state, payload) =>
      (state.misc_queue_completed = payload),

    SET_SP_QUEUE_COMPLETED: (state, payload) =>
      (state.sp_queue_completed = payload),

    SET_PPL_QUEUE_COMPLETED: (state, payload) =>
      (state.ppl_queue_completed = payload),

    SET_INITIAL_STATE: state => Object.assign(state, initialState()),

    SET_FORMDATA: (state, payload) => (state.formdata = payload),

    SET_CALCULATING: (state, payload) => (state.calculating = payload),

    SET_POSSIBLE_CITIES: (state, payload) => (state.possible_cities = payload),

    SET_FULL_ADDR: (state, payload) => (state.full_project_addr = payload),

    SET_POSSIBLE_CITIES_CONTRACTOR: (state, payload) =>
      (state.possible_cities_contractor = payload),

    SET_FULL_ADDR_CONTRACTOR: (state, payload) =>
      (state.full_project_addr_contractor = payload),

    SET_FORMDATA_ROOF_TYPE_ACTUAL: (state, payload) =>
      (state.formdata.project_meta.roof_type_actual = payload),

    SET_FORMDATA_BESS_ANCHORAGE_TYPE: (state, payload) =>
      (state.formdata.project_meta.bess_anchorage_type = payload),

    SET_FORMDATA_BUILDING_WIDTH: (state, payload) =>
      (state.formdata.project_meta.building_width = payload),

    SET_FORMDATA_BUILDING_LENGTH: (state, payload) =>
      (state.formdata.project_meta.building_length = payload),

    SET_FORMDATA_NUMBER_OF_STORIES: (state, payload) =>
      (state.formdata.project_meta.number_of_stories = payload),

    SET_FORMDATA_NUMBER_OF_PANELS: (state, payload) =>
      (state.formdata.project_meta.number_of_panels = payload),

    SET_FORMDATA_FULL_CALCS: (state, payload) =>
      (state.formdata.project_meta.full_calcs = payload),

    SET_FORMDATA_DESIGNER: (state, payload) =>
      (state.formdata.project_meta.designer = payload),

    SET_FORMDATA_DESIGNER_ARRAY: (state, payload) =>
      (state.formdata.project_meta.designer.array = payload),

    SET_FORMDATA_DESIGNER_CONTRACTOR: (state, payload) =>
      (state.formdata.project_meta.designer.contractor = payload),

    SET_IS_EXPRESS_DISABLED: (state, payload) =>
      (state.is_express_disabled = payload),

    SET_ENGINEERING_VALUES: (state, payload) =>
      (state.engineering_values = payload),

    SET_FORMDATA_SUMMARY_PROMO_CODES: (state, payload) =>
      (state.formdata.summary.promo_codes = payload),

    SET_FORMDATA_SUMMARY: (state, payload) =>
      (state.formdata.summary = payload),

    ADD_PROMO_CODE(state, code) {
      state.formdata.summary.promo_codes.push(code);
    },
    REMOVE_PROMO_CODE(state, index) {
      state.formdata.summary.promo_codes.splice(index, 1);
    }
  },
  actions: {
    setFormdataLocationAddress: async ({ commit, state }, payload) => {
      // CHECKING OF CITY before changing address
      const possible_cities = state.possible_cities;
      const possible_city_value = [
        "locality",
        "sublocality",
        "neighborhood",
        "administrative_area_level_3"
      ];

      let available_city = "";

      // whatever is available in the dropdown is the correct city
      possible_city_value.some(prop => {
        let possible_city = _.get(possible_cities, prop, null);

        if (possible_city) available_city = possible_city;

        return possible_city;
      });

      payload.address.city = available_city;

      commit("SET_FORMDATA_LOCATION", payload);
    },
    setFormdataLocationContractorAddress: async (
      { commit, state },
      payload
    ) => {
      // CHECKING OF CITY before changing address
      const possible_cities = state.possible_cities_contractor;
      const possible_city_value = [
        "locality",
        "sublocality",
        "neighborhood",
        "administrative_area_level_3"
      ];

      let available_city = "";

      // whatever is available in the dropdown is the correct city
      possible_city_value.some(prop => {
        let possible_city = _.get(possible_cities, prop, null);

        if (possible_city) available_city = possible_city;

        return possible_city;
      });

      payload.address.city = available_city;

      commit("SET_FORMDATA_DESIGNER_CONTRACTOR", payload);
    },

    applyParserResult({ commit, state, getters }, parser_result) {
      if (!parser_result) return;

      // Helper: case-insensitive name match against items with { values: { name } } structure
      const matchByName = (items, raw) => {
        if (!raw || !items?.length) return null;
        const needle = raw.toLowerCase();
        return (
          items.find(i => i.values?.name?.toLowerCase() === needle) ||
          items.find(
            i =>
              i.values?.name?.toLowerCase().includes(needle) ||
              needle.includes(i.values?.name?.toLowerCase())
          ) ||
          null
        );
      };

      // Helper: case-insensitive match against a plain string array
      const matchString = (options, raw) => {
        if (!raw) return null;
        const needle = raw.toLowerCase();
        return (
          options.find(o => o.toLowerCase() === needle) ||
          options.find(
            o =>
              o.toLowerCase().includes(needle) ||
              needle.includes(o.toLowerCase())
          ) ||
          null
        );
      };

      const loc = parser_result.location || {};
      const det = parser_result.details || {};
      const arr = parser_result.arrays || {};

      // Address is intentionally not autofilled here — it's already collected
      // manually in the Location step, which now runs before Upload.

      // ── Project meta ──────────────────────────────────────────────────────
      const meta = _.cloneDeep(state.formdata.project_meta);

      const matched_building = matchByName(
        state.building_descriptions,
        loc.solar_location?.value
      );
      if (matched_building) meta.building_description = matched_building.values;

      const matched_terrain = matchByName(
        state.surrounding_terrains,
        loc.surrounding_terrain?.value
      );
      if (matched_terrain) meta.surrounding_terrain = matched_terrain.values;

      const matched_roof = matchByName(state.roof_types, det.roof_type?.value);
      if (matched_roof) meta.roof_type = matched_roof.values;

      if (det.building_width?.value != null)
        meta.building_width = det.building_width.value;
      if (det.building_length?.value != null)
        meta.building_length = det.building_length.value;
      if (det.number_of_stories?.value != null)
        meta.number_of_stories = det.number_of_stories.value;
      if (det.number_of_panels?.value != null)
        meta.number_of_panels = det.number_of_panels.value;
      if (det.pitch_degrees?.value != null)
        meta.roof_slope = det.pitch_degrees.value;
      if (det.pitch_rise?.value != null) meta.type_pitch = det.pitch_rise.value;
      const matched_roof_material = matchByName(
        state.roof_types,
        det.roof_material_type?.value
      );
      if (matched_roof_material) meta.roof_type = matched_roof_material.values;

      // ── Arrays (Express/full-calcs only) ─────────────────────────────────
      const is_express = !!getters.is_express_selected;
      if (is_express && arr.data?.length) {
        const full_calcs = _.cloneDeep(
          state.formdata.project_meta.full_calcs
        ) || { array: [] };

        full_calcs.array = arr.data.map(a => {
          const existing = {};

          const framing_type = matchString(
            ["Rafter", "Truss"],
            a.roof_framing_type?.value
          );
          if (framing_type) existing.framing_type = framing_type;

          const roof_material_type = matchString(
            ["Composition Shingle", "Concrete 'Flat' or 'S' Tile"],
            a.roof_material_type?.value
          );
          if (roof_material_type)
            existing.roof_material_type = roof_material_type;

          const rafter_size = matchString(
            ["2x4", "2x6", "2x8", "2x10", "2x12"],
            a.roof_framing_type?.value
          );
          if (rafter_size) existing.rafter_size = rafter_size;

          if (a.framing_spacing?.value != null)
            existing.framing_spacing = a.framing_spacing.value;

          const decking = matchString(
            ["Roof Plywood", "T&G Decking"],
            a.decking?.value
          );
          if (decking) existing.decking = decking;

          const panel_orientation = matchString(
            ["Portrait", "Landscape"],
            a.pv_panel_orientation?.value
          );
          if (panel_orientation) existing.panel_orientation = panel_orientation;

          const pv_system = matchString(
            ["Railed", "Rail-less"],
            a.pv_system_type?.value
          );
          if (pv_system) existing.pv_system = pv_system;

          if (a.attachment_spacing?.value != null)
            existing.attachment_spacing = a.attachment_spacing.value;
          if (a.pitch_rise?.value != null)
            existing.type_pitch = a.pitch_rise.value;
          if (a.pitch_degrees?.value != null)
            existing.roof_slope = a.pitch_degrees.value;

          return existing;
        });

        meta.full_calcs = full_calcs;
      }

      commit("SET_FORMDATA_PROJECT_META", meta);

      // ── Details ───────────────────────────────────────────────────────────
      const details = _.cloneDeep(state.formdata.details);
      if (det.project_name?.value) details.name = det.project_name.value;
      if (det.client_project_number?.value)
        details.client_po = det.client_project_number.value;
      commit("SET_FORMDATA_DETAILS", details);
    }
  }
};
